/* hs-eslint ignored failing-rules */
/* eslint-disable hubspot-dev/no-unsafe-i18n-at-module-eval */

import { I18nInternal as I18n } from '../../internal/i18n-internal';
import localeMapper from './localeMapper';
import spacename from './spacename';
// @ts-ignore `./initializeI18nMethods` in untyped
import { initializeI18nMethods } from './initializeI18nMethods';

type $TSFixMe = any;
type $TSFixMeI2lLang = any;

initializeI18nMethods(I18n);

// This Set tracks which lang functions we've visited to ensure that we don't visit any
// lang file's function more than once.
let visitedFunctions = new Set<Function>();
export function __TEST_ONLY__clearVisitedFunctions() {
  visitedFunctions = new Set();
}

export type I18nTarget = {
  hasHadLoadingFailure?: boolean;
  translations: Record<string, any>;
};

export function createLoader(
  {
    context,
    source,
    mode,
  }: {
    context: $TSFixMe;
    source: string;
    mode: 'sync' | 'lazy';
  },
  type: 'pojo' | 'visit' = 'visit',
  // HACK: I18n.translations is marked as optional, but it is initialized in `initializeI18nMethods`
  // and so should always be defined by the time we reach here. We're using the type assertion to work
  // around this.
  target: I18nTarget = I18n as I18nTarget
) {
  if (!context) {
    throw new Error('invalid provider source');
  }

  const locales = Object.keys(context);

  function applyTranslations(t: $TSFixMe) {
    // Translation bundles can be null for apps/libs that don't have any keys in their en.lyaml:
    // https://git.hubteam.com/HubSpot/chatspot-ui/blob/master/chatspot-widget-iframe/static/lang/en.lyaml
    if (t !== null) {
      spacename(target.translations, t);
    }
  }

  function visitModule(mod: $TSFixMe) {
    if (mod) {
      if (type === 'pojo') {
        // In a jest context there is no "default" key for the default export.
        applyTranslations(mod.default || mod);
      }

      // Handle modules that export a visit function
      if (typeof mod.visit === 'function') {
        if (!visitedFunctions.has(mod.visit)) {
          visitedFunctions.add(mod.visit);
          mod.visit(visitModule, applyTranslations);
        }
      }

      if (mod.translations) {
        mod.translations.forEach(applyTranslations);
      }
    }

    return mod;
  }

  function loadContext(locale: string): Promise<$TSFixMe> {
    // TODO: This check is also done in the default localeMapper.
    // https://git.hubteam.com/HubSpot/I18n/blob/master/static/js/init/internal/localeMapper.ts
    // I'm guessing it's also here in case a different mapper is passed? We should see whether we can
    // dedupe it, as failing that check will simply cause the `load` to no-op.
    if (locales.indexOf(locale) < 0) {
      throw new Error(`locale ${locale} does not exist for ${source}`);
    }
    return context[locale]();
  }

  function loadSync(locale: string) {
    if (mode !== 'sync') {
      throw new Error(`${source} is not sync`);
    }

    return visitModule(loadContext(locale));
  }

  function loadLazy(locale: string) {
    if (mode !== 'lazy') {
      throw new Error(`${source} is not lazy`);
    }

    return loadContext(locale)
      .then(visitModule)
      .catch((error) => {
        target.hasHadLoadingFailure = true;
        setTimeout(() => {
          throw error;
        }, 0);
      });
  }

  function load(
    allLocales: string[],
    options: {
      localeMapper?(locale: string, locales: string[]): string | null;
    } = {}
  ) {
    const loadLocalePromises: Array<Promise<$TSFixMe>> = [];
    const map = options.localeMapper || localeMapper;
    const alreadyLoadedLocales: Record<string, boolean | undefined> = {};

    // TODO: This would be cleaner as a for loop
    allLocales.forEach((locale) => {
      const localeToLoad = map(locale, locales);

      // TODO: We can just pass locales through a Set to dedupe them
      const hasLoadedLocale = localeToLoad
        ? alreadyLoadedLocales[localeToLoad]
        : undefined;

      if (!hasLoadedLocale && localeToLoad && mode === 'lazy') {
        loadLocalePromises.push(loadLazy(localeToLoad));
        alreadyLoadedLocales[localeToLoad] = true;
      } else if (!hasLoadedLocale && localeToLoad) {
        loadLocalePromises.push(Promise.resolve(loadSync(localeToLoad)));
        alreadyLoadedLocales[localeToLoad] = true;
      }
    });

    // TODO: We should either consistently return an array or nothing.
    // Note that since we directly return the result of this promise.all to the caller of provider.register,
    // our public api is currently:
    //   resolve to (module | undefined)[] on partial success (failures are undefined)
    //   resolve to number on failure
    // We have consumers relying on this behavior (and there's currently no other way to tell success vs
    // failure), so I've added tests to ensure it doesn't change.
    // See https://git.hubteam.com/HubSpot/crm-sdk/blob/master/crm-cards/static/js/internal/CardTranslationsLoader.tsx#L194-L196
    return Promise.all(loadLocalePromises).catch((error) =>
      setTimeout(() => {
        throw error;
      }, 0)
    );
  }
  return { mode, load, locales, loadSync, loadLazy, visitModule };
}

export interface RegisterableIntlProvider {
  register(
    lang: $TSFixMeI2lLang,
    loadOptions?: {
      getLocales?(locales: string[]): string[];
      localeMapper?(locale: string, locales: string[]): string | null;
      // This refers to the interface of the data being loaded
      // - 'pojo': used for data in i18n-data, where the default export is a pojo
      // - 'visit': used for data from a lyaml file, which provides a named "visit" export
      // Note: There are other styles used by lyaml files (function default export, translations named
      // export), but "visit" is the most recent and will become the only style in the future.
      type?: 'pojo' | 'visit';
    }
  ): Promise<number | void | any[]>;
}

export interface IntlProvider extends RegisterableIntlProvider {
  setLocale(locales: string[]): void;
  langRegistry: Record<
    string,
    | {
        context: $TSFixMe;
        source: string;
        mode: 'sync' | 'lazy';
      }
    | undefined
  >;
  localePromise: Promise<string[]>;
}

export function create() {
  let setLocale = (__locales: string[]): void => {
    throw new Error('`setLocale()` called early');
  };

  const intl: IntlProvider = {
    langRegistry: {},
    localePromise: new Promise((resolve) => (setLocale = resolve)),
    setLocale,

    register(lang, loadOptions = {}) {
      if (this.langRegistry[lang.source]) {
        return Promise.resolve();
      }

      this.langRegistry[lang.source] = lang;
      const provider = createLoader(lang, loadOptions.type);

      if (process.env.NODE_ENV === 'development' && module.hot) {
        window.addEventListener(`i18n_reload_${lang.source}`, (event) => {
          const customEvent = event as CustomEvent<{ module: $TSFixMe }>;

          provider.visitModule(customEvent.detail.module);

          window.dispatchEvent(new Event('i18n_reload_complete'));
        });
      }

      return this.localePromise
        .then((allLocales) =>
          provider.load(
            loadOptions.getLocales
              ? loadOptions.getLocales(allLocales)
              : allLocales,
            loadOptions
          )
        )
        .catch((error) =>
          setTimeout(() => {
            throw error;
          }, 0)
        );
    },
  };

  return intl;
}
